import React, { useMemo, useState, useCallback } from 'react'
import styles from './gantt.module.scss'
import type { GanttTask } from './types'
import { durationDays, formatDate } from './utils/date'

export type TaskListProps = {
  tasks: GanttTask[]
  tz: string
  rowHeight: number
  collapsedIds?: string[]
  onToggleCollapse?: (id: string, collapsed: boolean) => void
  onRowClick?: (task: GanttTask) => void
  bodyRef?: React.RefObject<HTMLDivElement>
}

export type FlatTaskRow = {
  task: GanttTask
  level: number
  isVisible: boolean
}

type Node = GanttTask & { children: Node[] }

const buildTree = (tasks: GanttTask[]) => {
  const byId = new Map<string, Node>()
  tasks.forEach((t) => byId.set(t.id, { ...(t as GanttTask), children: [] }))
  const roots: Node[] = []
  byId.forEach((node) => {
    if (node.parentId && byId.has(node.parentId)) byId.get(node.parentId).children.push(node)
    else roots.push(node)
  })
  return roots
}

const flatten = (nodes: Node[], collapsed: Set<string>, level = 0, visible = true): FlatTaskRow[] => {
  const rows: FlatTaskRow[] = []
  for (const n of nodes) {
    rows.push({ task: n, level, isVisible: visible })
    const isCollapsed = collapsed.has(n.id)
    const childVisible = visible && !isCollapsed
    if (n.children?.length) rows.push(...flatten(n.children, collapsed, level + 1, childVisible))
  }
  return rows
}

export const useFlattenTasks = (tasks: GanttTask[], collapsedIds?: string[]) => {
  return useMemo(() => {
    const roots = buildTree(tasks)
    const collapsed = new Set(collapsedIds || [])
    return flatten(roots, collapsed)
  }, [tasks, collapsedIds])
}

export const TaskList = ({
  tasks,
  tz,
  rowHeight,
  collapsedIds,
  onToggleCollapse,
  onRowClick,
  bodyRef,
}: TaskListProps) => {
  const [internalCollapsed, setInternalCollapsed] = useState<string[]>([])
  const controlled = collapsedIds !== undefined
  const effectiveCollapsed = controlled ? collapsedIds : internalCollapsed

  const rows = useFlattenTasks(tasks, effectiveCollapsed)

  const handleToggle = useCallback(
    (task: GanttTask) => {
      const nextCollapsed = new Set(effectiveCollapsed)
      if (nextCollapsed.has(task.id)) nextCollapsed.delete(task.id)
      else nextCollapsed.add(task.id)
      const list = Array.from(nextCollapsed)
      if (controlled) onToggleCollapse?.(task.id, nextCollapsed.has(task.id))
      else setInternalCollapsed(list)
    },
    [effectiveCollapsed, controlled, onToggleCollapse]
  )

  return (
    <div className={styles.leftPane} style={{ ['--row-height' as any]: `${rowHeight}px` }}>
      <table className={['table', 'table-sm', 'mb-0', styles.table].join(' ')}>
        <thead>
          <tr>
            <th style={{ width: 48 }}>#</th>
            <th style={{ width: 240 }}>Task Name</th>
            <th style={{ width: 80 }}>Duration</th>
            <th style={{ width: 100 }}>Start</th>
            <th style={{ width: 100 }}>End</th>
          </tr>
        </thead>
      </table>
      <div className={styles.body} ref={bodyRef}>
        <table className={['table', 'table-sm', 'mb-0', styles.table].join(' ')}>
          <tbody>
            {rows
              .filter((r) => r.isVisible)
              .map((row, idx) => {
                const t = row.task
                const isParent = tasks.some((x) => x.parentId === t.id)
                const hasChildren = isParent
                const isCollapsed = effectiveCollapsed.includes(t.id)

                const duration = durationDays(t.start, t.end, tz)
                return (
                  <tr
                    key={t.id}
                    className={styles.row}
                    onClick={() => onRowClick?.(t)}
                    style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                  >
                    <td className={styles.rowNumberCell}>{idx + 1}</td>
                    <td className={styles.nameCell}>
                      <span className={styles.indent} style={{ ['--level' as any]: row.level }} />
                      {hasChildren && (
                        <button
                          className={styles.toggle}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleToggle(t)
                          }}
                          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                        >
                          {isCollapsed ? '▶' : '▼'}
                        </button>
                      )}
                      {t.name}
                    </td>
                    <td>{duration}d</td>
                    <td>{formatDate(t.start, tz)}</td>
                    <td>{formatDate(t.end, tz)}</td>
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
